import React, { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuthContext } from '../../contexts'

import styled from 'styled-components'

import { AdminUserBadge } from '../AdminUserBadge'
import { Item } from './Item'
import { Role as RolesEnum } from '../../generated/graphql'
import { hasRequiredRole } from '../../utils/roles'

export type Props = {
  children: React.ReactNode
  renderMenuExpansion?: (props: Props) => any
}

const Container = styled.div`
  width: 100%;
`

const LeftColumn = styled.div`
  background-color: #fff;
  border-right: 1px solid #e6e7e7;
  bottom: 0;
  height: auto;
  left: 0;
  position: fixed;
  top: 0;
  width: 100px;
  z-index: 1;
`

const RightColumn = styled.div`
  background-color: #f9f9fc;
  margin-left: 100px;
  position: relative;
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const Items = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  min-width: 100px;
  padding: 50px 0 30px;
`

const Topper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  display: inline-block;
  height: 70px;
  width: 70px;
`

const Logo = styled.img`
  cursor: pointer;
  margin-bottom: 100px;
  width: 26px;
`

export const Sidenav = (props: Props) => {
  const history = useHistory()
  const location = useLocation()

  const { children, renderMenuExpansion } = props

  const { adminUser } = useAuthContext()

  const hasSidekiqAccess = useMemo(() => {
    const roles: string[] | undefined = adminUser?.roles
    return hasRequiredRole([RolesEnum.ResqueConsole, RolesEnum.Admin], roles)
  }, [adminUser])

  const items = useMemo(() => {
    const buffer = []

    if (adminUser) {
      // ISS-578: Temporarily adding this behavior to admin portal until we can support global navigation customization
      // from micro app configuration.
      if (location.pathname.startsWith('/support')) {
        buffer.push({
          icon: 'search',
          href: '/',
          selected: location.pathname === '/support',
        })
      }

      if (hasSidekiqAccess) {
        buffer.push({
          icon: 'hdd-o',
          href:
            process.env.SIDEKIQ_CONSOLE_URL || 'https://admin.staging.acorns.io/sidekiq?locale=en',
          target: '_blank',
        })
      }
    }

    return buffer
  }, [adminUser, location])

  const handleHomeClick = () => {
    history.push('/')
  }

  return (
    <Container>
      <LeftColumn>
        <Items>
          <Topper>
            <Logo
              onClick={handleHomeClick}
              src={'https://signin.acorns.com/acorns-logo.9bec8634.svg'}
            />

            <nav>
              <List>
                {items.map((item, index) => (
                  <Item key={index} {...item} />
                ))}
              </List>
            </nav>
          </Topper>

          <Footer>
            {adminUser && (
              <AdminUserBadge onSignOut={() => history.push('/logout')} adminUser={adminUser} />
            )}
          </Footer>
        </Items>

        {renderMenuExpansion && renderMenuExpansion(props)}
      </LeftColumn>

      <RightColumn>{children}</RightColumn>
    </Container>
  )
}
